<template>
  <div class="flight-form">
    <template v-if="isLocked">
      <toolbar
        :backRoute="-1"
        title="Edit flight"
        :isSticky="true">
        <d-row class="px-4 pt-2 pb-2 w-100 mx-0">
          <div class="text-center w-100">
            <template v-if="data.departure_date">{{ prepareDate(data.departure_date.startDate) | displayDate }} </template>
            <template v-if="getAirportIcaoById(data.departure_airport_id) !== '???'">{{ getAirportIcaoById(data.departure_airport_id) }} </template>
            <template v-if="data.departure_date && data.arrival_date">{{ prepareDate(data.departure_date.startDate) | displayHour }} - {{ prepareDate(data.arrival_date.startDate) | displayHour }} </template>
            <template v-if="getAirportIcaoById(data.arrival_airport_id) !== '???'">{{ getAirportIcaoById(data.arrival_airport_id) }} </template>
            <template v-if="data.departure_date && data.arrival_date && prepareDate(data.departure_date.startDate).split(' ')[0] !== prepareDate(data.arrival_date.startDate).split(' ')[0]">
              {{ prepareDate(data.arrival_date.startDate) | displayDate }}
            </template>
          </div>
        </d-row>

        <d-row class="px-4 pt-3 pb-0 text-center">
          <d-col cols="4" v-if="!loadError && allDataLoaded">
            Aircraft reg: <strong class="text-primary">{{ aircraft_regin_mark }}</strong>
          </d-col>

          <d-col cols="4" v-if="!loadError && allDataLoaded">
            Trip Ref: <strong class="text-primary">{{ trip_reference }}</strong>
          </d-col>

          <d-col cols="4" v-if="!loadError && allDataLoaded">
            <d-checkbox
              inline
              v-model="data.is_confirmed"
              toggle>
              Is confirmed
            </d-checkbox>
          </d-col>
        </d-row>
      </toolbar>
    </template>
    <template v-else>
      <toolbar
        :backRoute="-1"
        :beforeBack="checkForChanges"
        title="Edit flight"
        deleteButtonLabel="Delete flight"
        deleteButtonEvent="flight-item-delete"
        saveButtonLabel="Save flight"
        saveButtonEvent="flight-item-save"
        :deleteInProgress="removeInProgress"
        :saveInProgress="saveInProgress"
        :isSticky="true">
        <d-row class="px-4 pt-2 pb-2 w-100 mx-0">
          <div class="text-center w-100">
            <template v-if="data.departure_date">{{ prepareDate(data.departure_date.startDate) | displayDate }} </template>
            <template v-if="getAirportIcaoById(data.departure_airport_id) !== '???'">{{ getAirportIcaoById(data.departure_airport_id) }} </template>
            <template v-if="data.departure_date && data.arrival_date">{{ prepareDate(data.departure_date.startDate) | displayHour }} - {{ prepareDate(data.arrival_date.startDate) | displayHour }} </template>
            <template v-if="getAirportIcaoById(data.arrival_airport_id) !== '???'">{{ getAirportIcaoById(data.arrival_airport_id) }} </template>
            <template v-if="data.departure_date && data.arrival_date && prepareDate(data.departure_date.startDate).split(' ')[0] !== prepareDate(data.arrival_date.startDate).split(' ')[0]">
              {{ prepareDate(data.arrival_date.startDate) | displayDate }}
            </template>
          </div>
        </d-row>

        <d-row class="px-4 pt-3 pb-0 text-center">
          <d-col cols="3" v-if="!loadError && allDataLoaded">
            Aircraft reg: <strong class="text-primary">{{ aircraft_regin_mark }}</strong>
          </d-col>

          <d-col
            v-if="!loadError && allDataLoaded"
            cols="3"
            class="d-flex">
            <d-input
              v-model="data.number"
              size="sm"
              placeholder="Additional flight number"
              style="margin-top: -5px;" />
          </d-col>

          <d-col cols="3" v-if="!loadError && allDataLoaded">
            Trip Ref: <strong class="text-primary">{{ trip_reference }}</strong>
          </d-col>

          <d-col cols="3" v-if="!loadError && allDataLoaded">
            <d-checkbox
              inline
              v-model="data.is_confirmed"
              toggle>
              Is confirmed
            </d-checkbox>
          </d-col>
        </d-row>
      </toolbar>
    </template>

    <d-alert
      v-if="isLocked"
      show
      theme="warning"
      class="text-center mt-5">
      This flight is currently edited by {{ editStatus.edit_by }}
    </d-alert>

    <d-container fluid :class="{ 'common-form px-0 pt-5 pb-5': true, 'is-locked': isLocked }">
      <d-row id="route" class="w-100 mt-0 mb-4 ml-0 mr-0">
        <fieldset class="w-100 mb-0">
          <legend>Route</legend>

          <d-row>
            <d-col cols="12">
              <span>Departure airport -
                <a :href="baseUrl + '/dashboard/airports/' + data.departure_airport_id" target="_blank" class="text-primary">
                  {{ getAirportById(data.departure_airport_id, true) }}
                </a>
              </span>
              <span class="ml-5">
                Arrival airport -
                <a :href="baseUrl + '/dashboard/airports/' + data.arrival_airport_id" target="_blank" class="text-primary">
                  {{ getAirportById(data.arrival_airport_id, true) }}
                </a>
              </span>
            </d-col>
          </d-row>

          <d-row class="mt-3">
            <d-col cols="4">
              <div class="form-group d-flex align-items-center">
                <label class="w-50">
                  Speed:
                </label>
                <d-input
                  v-model="data.route_speed"
                  class="w-50" />
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group d-flex align-items-center">
                <label class="w-50">
                  Burn:
                </label>
                <d-input
                  v-model="data.route_burn"
                  class="w-50" />
              </div>
            </d-col>

            <d-col cols="4">
              <div class="form-group d-flex align-items-center">
                <label class="w-50">
                  Flight level:
                </label>
                <d-input
                  v-model="data.route_flight_level"
                  class="w-50" />
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="12">
              <div class="form-group d-flex align-items-center">
                <label for="notes" class="w-16">Route:</label>
                <d-textarea
                  v-model="data.route_description"
                  :value.prop="data.route_description"
                  :rows="3"
                  class="w-83" />
              </div>
            </d-col>
          </d-row>
        </fieldset>
      </d-row>

      <checklist-list
        v-if="allDataLoaded"
        referenceType="FLIGHT"
        :referenceId="data.id"
        itemName="flight" />

      <validation-observer
        v-if="!loadError && allDataLoaded"
        ref="flight-form"
        tag="form">
        <d-row class="w-100 ml-0 mr-0" id="sector-overview">
          <fieldset class="w-100 mb-0">
            <legend>Sector overview</legend>

            <d-row>
              <d-col cols="12">
                <div class="form-group">
                  <label for="notes">Notes:</label>
                  <d-textarea
                    v-model="data.notes"
                    :value.prop="data.notes"
                    :rows="2" />
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </d-row>

        <d-row
          id="flight_details"
          hidden
          class="w-100 pt-4 ml-0 mr-0">
          <fieldset class="w-100 mb-0">
            <legend>Flight details</legend>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">Departure airport:</label>
                  <v-select
                    ref="airportsVSelectDeparture"
                    :options="$root.enabledAirports"
                    v-model="data.departure_airport_id"
                    :reduce="airport => airport.id"
                    placeholder="Select an airport"
                    :filterBy="filterByAirports"
                    :maxItemsDisplay="100"
                    label="id"
                    class="w-50">
                    <template slot="no-options">
                      <template>There are no airports for the provided phrase&hellip;</template>
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center">
                        {{ option.icao }} ({{ option.name }})
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center">
                        {{ option.icao }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </d-col>

              <d-col cols="5">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">Departure (ETD) (Z):</label>
                  <date-range-picker
                    ref="date-range-picker-d"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    v-model="data.departure_date"
                    :ranges="false"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-d')">
                  </date-range-picker>
                </div>

                <div class="form-group d-flex  align-items-center">
                  <label class="w-50">Departure (ETD) (Z) (Actual):</label>
                  <date-range-picker
                    ref="date-range-picker-dr"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    v-model="data.real_departure_date"
                    :ranges="false"
                    :time-picker-increment="1"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-dr')">
                  </date-range-picker>
                </div>
              </d-col>

              <d-col cols="3">
                <div class="form-group has-inactive-date-range d-flex align-items-center">
                  <label class="w-50">Local:</label>
                  <date-range-picker
                    ref="date-range-picker-dl"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    :disabled="true"
                    v-model="data.departure_local_date"
                    :ranges="false"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-dl')">
                  </date-range-picker>
                </div>

                <div class="form-group has-inactive-date-range d-flex align-items-center">
                  <label class="w-50">Local (Actual):</label>
                  <date-range-picker
                    ref="date-range-picker-dlr"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    :disabled="true"
                    v-model="data.real_departure_local_date"
                    :ranges="false"
                    :time-picker-increment="1"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-dlr')">
                  </date-range-picker>
                </div>
              </d-col>
            </d-row>

            <hr>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">Arrival airport:</label>
                  <v-select
                    ref="airportsVSelectArrival"
                    :options="$root.enabledAirports"
                    v-model="data.arrival_airport_id"
                    :reduce="airport => airport.id"
                    placeholder="Select an airport"
                    :filterBy="filterByAirports"
                    :maxItemsDisplay="100"
                    label="id"
                    class="w-50">
                    <template slot="no-options">
                      <template>There are no airports for the provided phrase&hellip;</template>
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center">
                        {{ option.icao }} ({{ option.name }})
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center">
                        {{ option.icao }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </d-col>

              <d-col cols="5">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">Arrival (ETD) (Z):</label>
                  <date-range-picker
                    ref="date-range-picker-a"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    v-model="data.arrival_date"
                    :ranges="false"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-a')">
                  </date-range-picker>
                </div>

                <div class="form-group d-flex  align-items-center">
                  <label class="w-50">Arrival (ETD) (Z) (Actual):</label>
                  <date-range-picker
                    ref="date-range-picker-ar"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    v-model="data.real_arrival_date"
                    :ranges="false"
                    :time-picker-increment="1"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-ar')">
                  </date-range-picker>
                </div>
              </d-col>

              <d-col cols="3">
                <div class="form-group has-inactive-date-range d-flex align-items-center">
                  <label class="w-50">Local:</label>
                  <date-range-picker
                    ref="date-range-picker-al"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    :disabled="true"
                    v-model="data.arrival_local_date"
                    :ranges="false"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-al')">
                  </date-range-picker>
                </div>

                <div class="form-group has-inactive-date-range d-flex align-items-center">
                  <label class="w-50">Local (Actual):</label>
                  <date-range-picker
                    ref="date-range-picker-alr"
                    :locale-data="{
                      'firstDay': 1,
                      'format': 'ddmmmyy HHMM'
                    }"
                    :class="{ 'w-50': true }"
                    :singleDatePicker="true"
                    :timePicker="true"
                    :showWeekNumbers="false"
                    :showDropdowns="true"
                    :autoApply="false"
                    :disabled="true"
                    v-model="data.real_arrival_local_date"
                    :ranges="false"
                    :time-picker-increment="1"
                    :linkedCalendars="true"
                    @toggle="togglePickers('date-range-picker-alr')">
                  </date-range-picker>
                </div>
              </d-col>
            </d-row>

            <hr>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-40">Type of flight:</label>

                  <div :class="{ 'form-control': true, 'is-select': true, 'w-60': true }">
                    <v-select
                      :options="[
                        { text: 'Scheduled Air Service', value: 'S' },
                        { text: 'Non-scheduled Air Transport', value: 'N' },
                        { text: 'General Aviation/Owner', value: 'G' },
                        { text: 'Military', value: 'M' },
                        { text: 'Other', value: 'X' }
                      ]"
                      :clearable="false"
                      v-model="data.flight_type"
                      :reduce="option => option.value"
                      label="text">
                    </v-select>
                  </div>
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">En route time:</label>
                  <d-input
                    v-model="data.route_time"
                    :readonly="true"
                    class="w-50" />
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-50">Number of pax:</label>
                  <d-input
                    type="number"
                    min="0"
                    step="1"
                    readonly
                    :value="calculatedPaxNumber"
                    class="w-50" />
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </d-row>

        <d-row class="w-100 pt-4 ml-0 mr-0" id="handlers">
          <fieldset class="w-100 mb-0">
            <legend>Handling agents</legend>

            <flight-form-handlers
              :airport-name="getAirportById(data.departure_airport_id, true)"
              :airport-id="data.departure_airport_id"
              not-required-field="departure_handler_not_required"
              :handling-agents="departureHandlingAgents"
              selected-handler-field="departure_handler_id"
              handler-requested-field="departure_handler_requested"
              handler-confirmed-field="departure_handler_confirmed"
              handler-payment-status-field="departure_handler_payment_status" />

            <flight-form-handlers
              :airport-name="getAirportById(data.arrival_airport_id, true)"
              :airport-id="data.arrival_airport_id"
              not-required-field="arrival_handler_not_required"
              :handling-agents="arrivalHandlingAgents"
              selected-handler-field="arrival_handler_id"
              handler-requested-field="arrival_handler_requested"
              handler-confirmed-field="arrival_handler_confirmed"
              handler-payment-status-field="arrival_handler_payment_status" />
          </fieldset>
        </d-row>

        <d-row
          id="slotdetails"
          class="w-100 mt-4 ml-0 mr-0">
          <fieldset class="w-100 mb-0">
            <legend>Slot details</legend>

            <d-row>
              <d-col cols="3">
                <span>Departure airport:</span><br>
                <a :href="baseUrl + '/dashboard/airports/' + data.departure_airport_id" target="_blank" class="text-primary">
                  {{ getAirportById(data.departure_airport_id, true) }}
                </a>
              </d-col>

              <d-col cols="5">
                <div class="form-group d-flex align-items-center">
                  <label>Tolerance:</label>
                  <d-input-group
                    prepend="Before"
                    class="ml-2">
                    <d-input
                      :value="slotTolerance.departure.required ? slotTolerance.departure.before : 'not required'"
                      :readonly="true"
                      class="text-center" />
                  </d-input-group>

                  <d-input-group
                    prepend="After"
                    class="ml-2">
                    <d-input
                      :value="slotTolerance.departure.required ? slotTolerance.departure.after : 'not required'"
                      :readonly="true"
                      class="text-center" />
                  </d-input-group>
                </div>
              </d-col>

              <d-col cols="4" class="align-items-center">
                <d-checkbox
                  inline
                  toggle-small
                  v-model="data.slot_departure_confirmed"
                  toggle>
                  Confirmed
                </d-checkbox>

                <d-checkbox
                  inline
                  toggle-small
                  v-model="data.slot_departure_not_required"
                  toggle>
                  Not required
                </d-checkbox>
              </d-col>
            </d-row>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-25">Requested:</label>
                  <div class="d-flex w-75">
                    <date-range-picker
                      ref="date-range-picker-srdt"
                      :locale-data="{
                        'firstDay': 1,
                        'format': 'ddmmmyy'
                      }"
                      :class="{ 'w-50': true, 'mr-1': true }"
                      :singleDatePicker="true"
                      :timePicker="false"
                      :showWeekNumbers="false"
                      :showDropdowns="true"
                      :autoApply="true"
                      v-model="data.slot_departure_requested_date"
                      :ranges="false"
                      :linkedCalendars="true"
                      @toggle="togglePickers('date-range-picker-srdt')">
                    </date-range-picker>

                    <validation-provider
                      rules="slot-time"
                      name="slot-departure-requested-time"
                      class="w-50"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.slot_departure_requested_time"
                        class="w-100 ml-1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-25">Granted:</label>
                  <div class="d-flex w-75">
                    <date-range-picker
                      ref="date-range-picker-sgdt"
                      :locale-data="{
                        'firstDay': 1,
                        'format': 'ddmmmyy'
                      }"
                      :class="{ 'w-50': true, 'mr-1': true }"
                      :singleDatePicker="true"
                      :timePicker="false"
                      :showWeekNumbers="false"
                      :showDropdowns="true"
                      :autoApply="true"
                      v-model="data.slot_departure_granted_date"
                      :ranges="false"
                      :linkedCalendars="true"
                      @toggle="togglePickers('date-range-picker-sgdt')">
                    </date-range-picker>

                    <validation-provider
                      rules="slot-time"
                      name="slot-departure-granted-time"
                      class="w-50"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.slot_departure_granted_time"
                        class="w-100 ml-1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </d-col>

              <d-col cols="4">
                <div class="d-flex align-items-center">
                  <label class="w-25">Notes:</label>
                  <d-input v-model="data.slot_departure_notes" class="w-75" />
                </div>
              </d-col>
            </d-row>

            <hr>

            <d-row>
              <d-col cols="3">
                <span>Arrival airport:</span><br>
                <a :href="baseUrl + '/dashboard/airports/' + data.arrival_airport_id" target="_blank" class="text-primary">
                  {{ getAirportById(data.arrival_airport_id, true) }}
                </a>
              </d-col>

              <d-col cols="5">
                <div class="form-group d-flex align-items-center">
                  <label>Tolerance:</label>
                  <d-input-group
                    prepend="Before"
                    class="ml-2">
                    <d-input
                      :value="slotTolerance.arrival.required ? slotTolerance.arrival.before : 'not required'"
                      :readonly="true"
                      class="text-center" />
                  </d-input-group>

                  <d-input-group
                    prepend="After"
                    class="ml-2">
                    <d-input
                      :value="slotTolerance.arrival.required ? slotTolerance.arrival.after : 'not required'"
                      :readonly="true"
                      class="text-center" />
                  </d-input-group>
                </div>
              </d-col>

              <d-col cols="4" class="align-items-center">
                <d-checkbox
                  inline
                  toggle-small
                  v-model="data.slot_arrival_confirmed"
                  toggle>
                  Confirmed
                </d-checkbox>

                <d-checkbox
                  inline
                  toggle-small
                  v-model="data.slot_arrival_not_required"
                  toggle>
                  Not required
                </d-checkbox>
              </d-col>
            </d-row>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-25">Requested:</label>
                  <div class="d-flex w-75">
                    <date-range-picker
                      ref="date-range-picker-srat"
                      :locale-data="{
                        'firstDay': 1,
                        'format': 'ddmmmyy'
                      }"
                      :class="{ 'w-50': true, 'mr-1': true }"
                      :singleDatePicker="true"
                      :timePicker="false"
                      :showWeekNumbers="false"
                      :showDropdowns="true"
                      :autoApply="true"
                      v-model="data.slot_arrival_requested_date"
                      :ranges="false"
                      :linkedCalendars="true"
                      @toggle="togglePickers('date-range-picker-srat')">
                    </date-range-picker>

                    <validation-provider
                      rules="slot-time"
                      name="slot-arrival-requested-time"
                      class="w-50"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.slot_arrival_requested_time"
                        class="w-100 ml-1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-25">Granted:</label>
                  <div class="d-flex w-75">
                    <date-range-picker
                      ref="date-range-picker-sgat"
                      :locale-data="{
                        'firstDay': 1,
                        'format': 'ddmmmyy'
                      }"
                      :class="{ 'w-50': true, 'mr-1': true }"
                      :singleDatePicker="true"
                      :timePicker="false"
                      :showWeekNumbers="false"
                      :showDropdowns="true"
                      :autoApply="true"
                      v-model="data.slot_arrival_granted_date"
                      :ranges="false"
                      :linkedCalendars="true"
                      @toggle="togglePickers('date-range-picker-sgat')">
                    </date-range-picker>

                    <validation-provider
                      rules="slot-time"
                      name="slot-arrival-granted-time"
                      class="w-50"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.slot_arrival_granted_time"
                        class="w-100 ml-1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center">
                  <label class="w-25">Notes:</label>
                  <d-input v-model="data.slot_arrival_notes" class="w-75" />
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </d-row>

        <permissions-list
          v-if="loadedData && setUpComplete"
          :departureAirport="getAirportById(data.departure_airport_id, true)"
          :departureAirportId="data.departure_airport_id"
          :arrivalAirport="getAirportById(data.arrival_airport_id, true)"
          :arrivalAirportId="data.arrival_airport_id"
          :referenceId="data.id"
          :aircraftReginMark="aircraft_regin_mark"
          :arrivalAirportData="arrivalAirport"
          :arrivalDate="loadedArrivalDate"
          :customerCompanyName="tripCustomerName"
          :departureAirportData="departureAirport"
          :departureDate="loadedDepartureDate" />

        <pax-list
          v-if="loadedData"
          id="crew"
          title="Crew"
          type="crew"
          :departureAirport="getAirportById(data.departure_airport_id, true)"
          :departureAirportId="data.departure_airport_id"
          :departureAirportCountryId="getAirportCountryIdById(data.departure_airport_id)"
          :arrivalAirport="getAirportById(data.arrival_airport_id, true)"
          :arrivalAirportId="data.arrival_airport_id"
          :arrivalAirportCountryId="getAirportCountryIdById(data.arrival_airport_id)"
          :referenceId="data.id"
          :tripReference="trip_reference"
          :loadPassengersItems="false"
          :loadCrewItems="true"
          :paxLimit="-1"
          :customerId="data.customer_id"
          :departureHotelsNotRequired="data.crew_departure_hotels_not_required"
          :arrivalHotelsNotRequired="data.crew_arrival_hotels_not_required"
          :departureTransportNotRequired="data.crew_departure_transport_not_required"
          :arrivalTransportNotRequired="data.crew_arrival_transport_not_required" />

        <pax-list
          v-if="loadedData"
          id="passengers"
          title="Passengers"
          type="passenger"
          :departureAirport="getAirportById(data.departure_airport_id, true)"
          :departureAirportId="data.departure_airport_id"
          :departureAirportCountryId="getAirportCountryIdById(data.departure_airport_id)"
          :arrivalAirport="getAirportById(data.arrival_airport_id, true)"
          :arrivalAirportId="getAirportCountryIdById(data.arrival_airport_id)"
          :arrivalAirportCountryId="getAirportCountryIdById(data.arrival_airport_id)"
          :referenceId="data.id"
          :tripReference="trip_reference"
          :tbcCount="data.tbc_count"
          :loadPassengersItems="true"
          :loadCrewItems="false"
          :customerId="data.customer_id"
          :departureHotelsNotRequired="data.passengers_departure_hotels_not_required"
          :arrivalHotelsNotRequired="data.passengers_arrival_hotels_not_required"
          :departureTransportNotRequired="data.passengers_departure_transport_not_required"
          :arrivalTransportNotRequired="data.passengers_arrival_transport_not_required" />

        <d-row class="w-100 mt-4 ml-0 mr-0" id="fuel">
          <fieldset class="w-100 mb-0">
            <legend>Fuel</legend>

            <d-row>
              <d-col cols="12" lg="12" md="12">
                <h6>
                  <a :href="baseUrl + '/dashboard/airports/' + data.departure_airport_id" target="_blank" class="text-primary">
                    Airport - {{ getAirportById(data.departure_airport_id, true) }}
                  </a>
                </h6>
              </d-col>
            </d-row>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center mb-0">
                  <label class="w-25">Fueler:</label>
                  <d-input
                    v-model="data.departure_fuel_fueler"
                    class="w-75" />
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center mb-0">
                  <label class="w-25">Price:</label>
                  <d-input
                    v-model="data.departure_fuel_price"
                    class="w-75" />
                </div>
              </d-col>

              <d-col cols="2" class="d-flex align-items-center">
                <d-checkbox
                  inline
                  v-model="data.departure_fuel_confirmed"
                  toggle
                  class="mb-0">
                  Confirmed
                </d-checkbox>
              </d-col>

              <d-col cols="2" class="d-flex align-items-center">
                <d-checkbox
                  inline
                  v-model="data.departure_fuel_not_required"
                  toggle
                  class="mb-0">
                  Not required
                </d-checkbox>
              </d-col>
            </d-row>

            <hr>

            <d-row>
              <d-col cols="12" lg="12" md="12">
                <h6>
                  <a :href="baseUrl + '/dashboard/airports/' + data.arrival_airport_id" target="_blank" class="text-primary">
                    Airport - {{ getAirportById(data.arrival_airport_id, true) }}
                  </a>
                </h6>
              </d-col>
            </d-row>

            <d-row>
              <d-col cols="4">
                <div class="form-group d-flex align-items-center mb-0">
                  <label class="w-25">Fueler:</label>
                  <d-input
                    v-model="data.arrival_fuel_fueler"
                    class="w-75" />
                </div>
              </d-col>

              <d-col cols="4">
                <div class="form-group d-flex align-items-center mb-0">
                  <label class="w-25">Price:</label>
                  <d-input
                    v-model="data.arrival_fuel_price"
                    class="w-75" />
                </div>
              </d-col>

              <d-col cols="2" class="d-flex align-items-center">
                <d-checkbox
                  inline
                  v-model="data.arrival_fuel_confirmed"
                  toggle
                  class="mb-0">
                  Confirmed
                </d-checkbox>
              </d-col>

              <d-col cols="2" class="d-flex align-items-center">
                <d-checkbox
                  inline
                  v-model="data.arrival_fuel_not_required"
                  toggle
                  class="mb-0">
                  Not required
                </d-checkbox>
              </d-col>
            </d-row>

            <hr>

            <d-row>
              <d-col cols="12">
                <div class="form-group d-flex align-items-center">
                  <label for="fuel_common_info" class="w-20">Fuel notes:</label>
                  <d-textarea
                    v-model="data.fuel_notes"
                    :value.prop="data.fuel_notes"
                    :rows="5"
                    class="w-80" />
                </div>
              </d-col>
            </d-row>

            <d-row>
              <d-col cols="12">
                <div class="form-group">
                  <label for="fuel_common_info">Additional informations:</label>
                  <table class="d-table w-100 mb-3">
                    <tbody>
                      <tr
                        v-for="(row, index) of data.fuel_additional_informations"
                        :key="'fuel-additional-informations-' + index">
                        <td
                          class="align-top pr-3 pb-3"
                          style="width: 240px;">
                          <d-input
                            v-model="row.key" />
                        </td>
                        <td class="align-top pr-0 pb-3">
                          <d-textarea
                            v-model="row.value"
                            :value.prop="row.value"
                            rows="4" />
                        </td>
                        <td
                          class="align-top pr-0 pb-3 text-right"
                          style="width: 60px;">
                          <d-button
                            theme="danger"
                            @click.prevent="data.fuel_additional_informations.splice(index, 1)">
                            <i class="material-icons">delete</i>
                          </d-button>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <d-button
                    theme="success"
                    outline
                    class="w-100"
                    @click.prevent="data.fuel_additional_informations.push({key: '', value: ''})">
                    Add row
                  </d-button>
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </d-row>

        <d-row
          v-if="data.is_first_flight_in_trip"
          class="w-100 mt-4 ml-0 mr-0"
          id="arrival-handling-request-data">
          <fieldset class="w-100 mb-0">
            <legend>Arrival Handling Request Data</legend>

            <d-row class="pb-2">
              <d-col cols="12">
                <div class="form-group d-flex align-items-center mb-0">
                  <label class="w-25">Invoice:</label>
                  <d-input
                    v-model="data.arrival_handling_request_invoice"
                    class="w-75" />
                </div>
              </d-col>
            </d-row>
          </fieldset>
        </d-row>
      </validation-observer>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
        Loading data&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
      </d-alert>
    </d-container>

    <add-crew-popup
      v-if="displayCrewPopup"
      :customerID="trip_customer_id" />

    <add-passenger-popup
      v-if="displayPassengerPopup"
      :customerID="trip_customer_id" />
  </div>
</template>

<script>
import AddCrewPopup from '../form-parts/AddCrewPopup.vue';
import AddPassengerPopup from '../form-parts/AddPassengerPopup.vue';
import ChecklistList from './../form-parts/CheckLists.vue';
import DateRangePicker from 'vue2-daterange-picker';
import DateUtils from './../../utils/DateUtils.js';
import FlightFormHandlers from './../form-parts/FlightFormHandlers';
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import ListUtils from './../../utils/ListUtils.js';
import PaxList from './../form-parts/PaxList';
import PermissionsList from './../form-parts/Permissions';
import Vue from 'vue';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'flight-form',
  mixins: [
    HasChangesDetection
  ],
  filters: {
    displayHour (value) {
      if (value) {
        value = value.split(' ')[1];
        value = value.split(':');
        value = [value[0], value[1]].join('');
        return value;
      }

      return '';
    },
    displayDate (value) {
      if (value) {
        let input = new Date(value);

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('');
      }

      return '';
    }
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.$root.loadedAirports && this.loadedHandlers && this.loadedEditStatus;
    },
    arrivalAirport () {
      let index = this.$root.airports.findIndex(airport => airport.id === this.data.arrival_airport_id);

      if (index > -1) {
        return this.$root.airports[index];
      }

      return false;
    },
    arrivalHandlingAgents () {
      let index = this.$root.airports.findIndex(airport => airport.id === this.data.arrival_airport_id);

      if (index > -1) {
        let handlerIDs = this.$root.airports[index].handlers;
        return this.handlers.filter(handler => handlerIDs.indexOf(handler.id) > -1).map(handler => {
          handler.is_selected = this.data.arrival_handler_id === handler.id;
          return handler;
        });
      }

      return [];
    },
    departureAirport () {
      let index = this.$root.airports.findIndex(airport => airport.id === this.data.departure_airport_id);

      if (index > -1) {
        return this.$root.airports[index];
      }

      return false;
    },
    departureHandlingAgents () {
      let index = this.$root.airports.findIndex(airport => airport.id === this.data.departure_airport_id);

      if (index > -1) {
        let handlerIDs = this.$root.airports[index].handlers;
        return this.handlers.filter(handler => handlerIDs.indexOf(handler.id) > -1).map(handler => {
          handler.is_selected = this.data.departure_handler_id === handler.id;
          return handler;
        });
      }

      return [];
    },
    calculatedPaxNumber () {
      return parseInt(this.real_pax_number, 10) + parseInt(this.data.tbc_count, 10);
    },
    baseUrl () {
      return window.location.protocol + '//' + window.location.host;
    }
  },
  components: {
    'add-crew-popup': AddCrewPopup,
    'add-passenger-popup': AddPassengerPopup,
    'checklist-list': ChecklistList,
    'date-range-picker': DateRangePicker,
    'flight-form-handlers': FlightFormHandlers,
    'pax-list': PaxList,
    'permissions-list': PermissionsList
  },
  data () {
    return {
      data: {
        trip_id: 0,
        id: 0,
        customer_id: 0,
        number: '',
        notes: '',
        is_confirmed: true,
        arrival_airport_id: null,
        departure_airport_id: null,
        flight_type: 'G',
        route_time: '',
        pax_number: 0,
        tbc_count: 0,
        departure_date: null,
        departure_local_date: null,
        real_departure_date: null,
        real_departure_local_date: null,
        arrival_date: null,
        arrival_local_date: null,
        real_arrival_date: null,
        real_arrival_local_date: null,
        slot_departure_confirmed: false,
        slot_departure_not_required: false,
        slot_departure_notes: '',
        slot_departure_requested_date: null,
        slot_departure_requested_time: '',
        slot_departure_granted_date: null,
        slot_departure_granted_time: '',
        slot_arrival_confirmed: false,
        slot_arrival_not_required: false,
        slot_arrival_notes: '',
        slot_arrival_requested_date: null,
        slot_arrival_requested_time: '',
        slot_arrival_granted_date: null,
        slot_arrival_granted_time: '',
        departure_fuel_fueler: '',
        departure_fuel_price: '',
        departure_fuel_confirmed: false,
        departure_fuel_not_required: false,
        departure_handler_requested: false,
        departure_handler_confirmed: false,
        departure_handler_payment_status: null,
        route_speed: '',
        route_burn: '',
        route_flight_level: '',
        route_description: '',
        arrival_fuel_fueler: '',
        arrival_fuel_price: '',
        arrival_fuel_confirmed: false,
        arrival_fuel_not_required: false,
        arrival_handler_not_required: false,
        arrival_handler_requested: false,
        arrival_handler_confirmed: false,
        arrival_handler_payment_status: null,
        departure_handler_not_required: false,
        crew_departure_hotels_not_required: false,
        crew_departure_transport_not_required: false,
        crew_arrival_hotels_not_required: false,
        crew_arrival_transport_not_required: false,
        crew_departure_transport: '',
        crew_arrival_transport: '',
        crew_hotel: '',
        passengers_departure_hotels_not_required: false,
        passengers_departure_transport_not_required: false,
        passengers_arrival_hotels_not_required: false,
        passengers_arrival_transport_not_required: false,
        passengers_departure_transport: '',
        passengers_arrival_transport: '',
        passengers_hotel: '',
        fuel_additional_informations: [],
        fuel_notes: '',
        is_first_flight_in_trip: false,
        arrival_handling_request_invoice: ''
      },
      loadedArrivalDate: '',
      loadedDepartureDate: '',
      tripCustomerName: '',
      isLocked: false,
      trip_reference: '',
      aircraft_regin_mark: '',
      handlers: [],
      editStatus: null,
      loadedEditStatus: false,
      loadError: false,
      loadedData: false,
      loadedHandlers: false,
      removeInProgress: false,
      saveInProgress: false,
      real_pax_number: 0,
      setUpComplete: false,
      slotTolerance: {
        arrival: {
          before: '',
          after: '',
          required: false
        },
        departure: {
          before: '',
          after: '',
          required: false
        }
      },
      displayCrewPopup: false,
      displayPassengerPopup: false,
      trip_customer_id: null
    };
  },
  watch: {
    'data.departure_date': function (newValue) {
      this.recalculateRouteTime();

      if (this.setUpComplete) {
        this.data.slot_departure_requested_date = {
          startDate: new Date(newValue.startDate),
          endDate: new Date(newValue.endDate)
        };
        this.data.slot_departure_requested_time = [newValue.startDate.getHours(), newValue.startDate.getMinutes()].map(n => n < 10 ? '0' + n : n).join('');
      }
    },
    'data.arrival_date': function (newValue) {
      this.recalculateRouteTime();

      if (this.setUpComplete) {
        this.data.slot_arrival_requested_date = {
          startDate: new Date(newValue.startDate),
          endDate: new Date(newValue.endDate)
        };
        this.data.slot_arrival_requested_time = [newValue.startDate.getHours(), newValue.startDate.getMinutes()].map(n => n < 10 ? '0' + n : n).join('');
      }
    },
    'data.arrival_airport_id': function (newValue) {
      if (this.setUpComplete) {
        let airportData = this.$root.airports.filter(airport => airport.id === newValue);

        if (airportData[0]) {
          this.slotTolerance.arrival.required = airportData[0].slot_required;
          this.slotTolerance.arrival.before = airportData[0].arrival_slot_tolerance_before;
          this.slotTolerance.arrival.after = airportData[0].arrival_slot_tolerance_after;
        }
      }
    },
    'data.departure_airport_id': function (newValue) {
      if (this.setUpComplete) {
        let airportData = this.$root.airports.filter(airport => airport.id === newValue);

        if (airportData[0]) {
          this.slotTolerance.departure.required = airportData[0].slot_required;
          this.slotTolerance.departure.before = airportData[0].departure_slot_tolerance_before;
          this.slotTolerance.departure.after = airportData[0].departure_slot_tolerance_after;
        }
      }
    },
    'allDataLoaded': function (newValue, oldValue) {
      Vue.nextTick(() => {
        this.$bus.$emit('pax-list-passengers-hotel', this.data.passengers_hotel);
        this.$bus.$emit('pax-list-passengers-arrival-transport', this.data.passengers_arrival_transport);
        this.$bus.$emit('pax-list-passengers-departure-transport', this.data.passengers_departure_transport);
        this.$bus.$emit('pax-list-crew-hotel', this.data.crew_hotel);
        this.$bus.$emit('pax-list-crew-arrival-transport', this.data.crew_arrival_transport);
        this.$bus.$emit('pax-list-crew-departure-transport', this.data.crew_departure_transport);
      });

      let allowedSections = ['flight_details', 'slotdetails', 'permissions', 'crew', 'passengers', 'handlers'];

      if (newValue !== oldValue && newValue && this.$route.params.section_name) {
        if (allowedSections.indexOf(this.$route.params.section_name) === -1) {
          return;
        }

        setTimeout(() => {
          document.getElementById(this.$route.params.section_name).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
    }
  },
  mounted () {
    this.loadData();
    this.$bus.$on('flight-item-delete', this.removeFlight);
    this.$bus.$on('flight-item-save', this.validateForm);
    this.$bus.$on('flight-form-handler-update', this.setHandler);
    this.$bus.$on('flight-form-not-required-update', this.setNotRequired);
    this.$bus.$on('flight-form-transport-update', this.setTransport);
    this.$bus.$on('flight-form-hotel-update', this.setHotel);
    this.$bus.$on('flight-item-update-pax-number', this.updatePaxNumber);
    this.$bus.$on('flight-item-update-tbc-count', this.updateTbcCount);
    this.$bus.$on('add-crew-popup-show', this.showCrewPopup);
    this.$bus.$on('add-crew-popup-close', this.closeCrewPopup);
    this.$bus.$on('add-passenger-popup-show', this.showPassengerPopup);
    this.$bus.$on('add-passenger-popup-close', this.closePassengerPopup);
  },
  methods: {
    loadData () {
      let self = this;

      FormUtils.loadItemData(this, {
        endpointBase: '/trips/flights/item/',
        id: parseInt(this.$route.params.flight_id, 10),
        instanceFields: [
          'id',
          'notes',
          'number',
          'is_confirmed',
          'trip_reference',
          'aircraft_regin_mark',
          'departure_airport_id',
          'arrival_airport_id',
          'flight_type',
          'route_time',
          'pax_number',
          'tbc_count',
          'departure_date',
          'departure_local_date',
          'real_departure_date',
          'real_departure_local_date',
          'arrival_date',
          'arrival_local_date',
          'real_arrival_date',
          'real_arrival_local_date',
          'slot_departure_confirmed',
          'slot_departure_not_required',
          'slot_departure_notes',
          'slot_departure_requested_date',
          'slot_departure_requested_time',
          'slot_departure_granted_date',
          'slot_departure_granted_time',
          'slot_arrival_confirmed',
          'slot_arrival_not_required',
          'slot_arrival_notes',
          'slot_arrival_requested_date',
          'slot_arrival_requested_time',
          'slot_arrival_granted_date',
          'slot_arrival_granted_time',
          'crew_arrival_transport',
          'crew_departure_transport',
          'crew_hotel',
          'departure_fuel_fueler',
          'departure_fuel_price',
          'departure_fuel_confirmed',
          'departure_fuel_not_required',
          'departure_handler_requested',
          'departure_handler_confirmed',
          'departure_handler_payment_status',
          'route_speed',
          'route_burn',
          'route_flight_level',
          'route_description',
          'arrival_fuel_fueler',
          'arrival_fuel_price',
          'arrival_fuel_confirmed',
          'arrival_fuel_not_required',
          'arrival_handler_not_required',
          'arrival_handler_requested',
          'arrival_handler_confirmed',
          'arrival_handler_payment_status',
          'departure_handler_not_required',
          'crew_departure_hotels_not_required',
          'crew_departure_transport_not_required',
          'crew_arrival_hotels_not_required',
          'crew_arrival_transport_not_required',
          'passengers_departure_hotels_not_required',
          'passengers_departure_transport_not_required',
          'passengers_arrival_hotels_not_required',
          'passengers_arrival_transport_not_required',
          'passengers_arrival_transport',
          'passengers_departure_transport',
          'passengers_hotel',
          'fuel_additional_informations',
          'is_first_flight_in_trip'
        ],
        loadedKey: 'loadedData',
        booleanFields: [
          'is_confirmed',
          'departure_fuel_confirmed',
          'departure_fuel_not_required',
          'arrival_fuel_confirmed',
          'arrival_fuel_not_required',
          'slot_departure_confirmed',
          'slot_arrival_confirmed',
          'arrival_handler_not_required',
          'departure_handler_not_required',
          'passengers_arrival_transport_not_required',
          'passengers_arrival_hotels_not_required',
          'passengers_departure_transport_not_required',
          'passengers_departure_hotels_not_required',
          'crew_arrival_transport_not_required',
          'crew_arrival_hotels_not_required',
          'crew_departure_transport_not_required',
          'crew_departure_hotels_not_required',
          'arrival_handler_requested',
          'arrival_handler_confirmed',
          'departure_handler_requested',
          'departure_handler_confirmed',
          'slot_departure_confirmed',
          'slot_departure_not_required',
          'slot_arrival_confirmed',
          'slot_arrival_not_required',
          'is_first_flight_in_trip'
        ],
        successAction (response) {
          self.trip_reference = response.trip_reference;
          self.aircraft_regin_mark = response.aircraft_regin_mark;
          self.data.trip_id = response.trip_id;
          self.data.departure_date = { startDate: response.departure_date ? new Date(response.departure_date) : null };
          self.data.departure_local_date = { startDate: response.departure_local_date ? new Date(response.departure_local_date) : null };
          self.data.real_departure_date = { startDate: response.real_departure_date ? new Date(response.real_departure_date) : null };
          self.data.real_departure_local_date = { startDate: response.real_departure_local_date ? new Date(response.real_departure_local_date) : null };
          self.data.slot_departure_notes = response.slot_departure_notes;
          self.data.slot_departure_requested_date = { startDate: response.slot_departure_requested_date ? new Date(response.slot_departure_requested_date) : null };
          self.data.slot_departure_requested_time = response.slot_departure_requested_time;
          self.data.slot_departure_granted_date = { startDate: response.slot_departure_granted_date ? new Date(response.slot_departure_granted_date) : null };
          self.data.slot_departure_granted_time = response.slot_departure_granted_time;
          self.data.arrival_date = { startDate: response.arrival_date ? new Date(response.arrival_date) : null };
          self.data.arrival_local_date = { startDate: response.arrival_local_date ? new Date(response.arrival_local_date) : null };
          self.data.real_arrival_date = { startDate: response.real_arrival_date ? new Date(response.real_arrival_date) : null };
          self.data.real_arrival_local_date = { startDate: response.real_arrival_local_date ? new Date(response.real_arrival_local_date) : null };
          self.data.arrival_handler_id = response.arrival_handler_id;
          self.data.slot_arrival_notes = response.slot_arrival_notes;
          self.data.slot_arrival_requested_date = { startDate: response.slot_arrival_requested_date ? new Date(response.slot_arrival_requested_date) : null };
          self.data.slot_arrival_requested_time = response.slot_arrival_requested_time;
          self.data.slot_arrival_granted_date = { startDate: response.slot_arrival_granted_date ? new Date(response.slot_arrival_granted_date) : null };
          self.data.slot_arrival_granted_time = response.slot_arrival_granted_time;
          self.data.departure_handler_id = response.departure_handler_id;
          self.real_pax_number = response.pax_number - response.tbc_count;
          self.data.customer_id = response.trip.customer.id;
          // slot tolerance
          self.slotTolerance.arrival.required = response.arrival_airport.slot_required;
          self.slotTolerance.arrival.before = response.arrival_airport.arrival_slot_tolerance_before;
          self.slotTolerance.arrival.after = response.arrival_airport.arrival_slot_tolerance_after;
          self.slotTolerance.departure.required = response.departure_airport.slot_required;
          self.slotTolerance.departure.before = response.departure_airport.departure_slot_tolerance_before;
          self.slotTolerance.departure.after = response.departure_airport.departure_slot_tolerance_after;
          // fuel notes
          self.data.fuel_notes = response.trip.fuel_notes;

          if (response.is_first_flight_in_trip) {
            self.data.arrival_handling_request_invoice = response.arrival_handling_request_invoice;
          }

          if (response.trip) {
            self.trip_customer_id = response.trip.customer_id;
          }

          try {
            self.data.fuel_additional_informations = JSON.parse(response.fuel_additional_informations);
          } catch (err) {
            self.data.fuel_additional_informations = [];
          }

          delete self.data.trip_reference;
          delete self.data.aircraft_regin_mark;

          // Additional data
          self.loadedArrivalDate = response.arrival_date;
          self.loadedDepartureDate = response.departure_date;

          if (response.trip && response.trip.customer) {
            self.tripCustomerName = response.trip.customer.name;
          }

          Vue.nextTick(() => {
            self.setUpComplete = true;
            self.initPickerMonths();
          });
        }
      });

      this.loadAdditionalData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/trips/flights/editing-status/' + parseInt(this.$route.params.flight_id, 10),
        loadedKey: 'loadedEditStatus',
        outputKey: 'editStatus',
        errorKey: 'loadError',
        responseKey: 'status',
        noPagination: true,
        successAction: () => {
          if (!this.editStatus.edit_in_progress) {
            this.lockFlight();
          }

          if (this.editStatus.edit_by_other_user) {
            this.isLocked = true;
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/handlers/items?simple=1',
        params: {
          pagination: 0,
          where: {
            type: 'HANDLER'
          }
        },
        loadedKey: 'loadedHandlers',
        outputKey: 'handlers',
        errorKey: 'loadError'
      });
    },
    lockFlight () {
      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          edit_in_progress: 1
        },
        endpoint: '/trips/flights/set-editing-status/' + parseInt(this.$route.params.flight_id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during creating a lock on this flight item. Please try again',
        successAction: () => false
      });
    },
    unlockFlight () {
      FormUtils.submitInBackground({
        $http: this.$http,
        formData: {
          edit_in_progress: 0
        },
        endpoint: '/trips/flights/set-editing-status/' + parseInt(this.data.id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during creating a lock on this flight item. Please try again',
        successAction: () => false
      });
    },
    removeFlight () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/trips/flights/delete/',
        id: parseInt(this.data.id, 10),
        successTitle: 'Flight removed',
        successText: 'The selected flight has been removed',
        confirmTitle: 'Do you really want to remove this flight?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.go(-1);
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['flight-form'], this.submitForm);
    },
    prepareDate (date, dateField = null) {
      if (!date) {
        return null;
      }

      if (dateField && !date[dateField]) {
        return null;
      }

      date = new Date(date);
      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') + ' ' + [date.getHours(), date.getMinutes(), 0].map(n => n < 10 ? '0' + n : n).join(':');
    },
    prepareDateWithoutTime (date) {
      if (!date) {
        return null;
      }

      date = new Date(date);
      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareFlightData () {
      let data = JSON.parse(JSON.stringify(this.data));
      delete data.pax_number;
      delete data.tbc_count;

      data.departure_date = this.prepareDate(data.departure_date.startDate);
      data.real_departure_date = this.prepareDate(data.real_departure_date.startDate);
      data.arrival_date = this.prepareDate(data.arrival_date.startDate);
      data.real_arrival_date = this.prepareDate(data.real_arrival_date.startDate);
      data.slot_arrival_granted_date = this.prepareDateWithoutTime(data.slot_arrival_granted_date.startDate);
      data.slot_departure_granted_date = this.prepareDateWithoutTime(data.slot_departure_granted_date.startDate);
      data.slot_arrival_requested_date = this.prepareDateWithoutTime(data.slot_arrival_requested_date.startDate);
      data.slot_departure_requested_date = this.prepareDateWithoutTime(data.slot_departure_requested_date.startDate);

      if (data.arrival_handler_not_required) {
        data.arrival_handler_id = 0;
      }

      if (data.departure_handler_not_required) {
        data.departure_handler_id = 0;
      }

      if (data.departure_handler_id === null) {
        data.departure_handler_id = 0;
      }

      if (data.arrival_handler_id === null) {
        data.arrival_handler_id = 0;
      }

      data.fuel_additional_informations = JSON.stringify(this.data.fuel_additional_informations);

      if (!data.is_first_flight_in_trip) {
        delete data.arrival_handling_request_invoice;
      }

      return data;
    },
    submitForm () {
      let preparedData = this.prepareFlightData();

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/trips/flights/update/' + parseInt(this.data.id, 10),
        formData: preparedData,
        successTitle: 'Flight saved',
        successText: 'The flight has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        timer: 1200,
        successAction: () => {
          this.$bus.$emit('calendar-flights-update');
          this.updateOriginalData();
        }
      });
    },
    filterByAirports (option, label, search) {
      return (option.name + ' ' + option.icao).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    getAirportById (airportID, addName = false) {
      let index = this.$root.airports.findIndex(airport => airport.id === airportID);

      if (index > -1) {
        if (addName) {
          return this.$root.airports[index].iata + ' - ' + this.$root.airports[index].name;
        }

        return this.$root.airports[index].iata;
      }

      return '???';
    },
    getAirportIcaoById (airportID, addName = false) {
      let index = this.$root.airports.findIndex(airport => airport.id === airportID);

      if (index > -1) {
        if (addName) {
          return this.$root.airports[index].icao + ' - ' + this.$root.airports[index].name;
        }

        return this.$root.airports[index].icao;
      }

      return '???';
    },
    getAirportCountryIdById (airportID) {
      let index = this.$root.airports.findIndex(airport => airport.id === airportID);

      if (index > -1) {
        return this.$root.airports[index].country_id;
      }

      return null;
    },
    recalculateRouteTime () {
      let diff = this.data.arrival_date.startDate.getTime() - this.data.departure_date.startDate.getTime();
      diff = Math.floor(diff / 1000);
      diff /= 60;
      let hours = Math.floor(diff / 60);
      let minutes = Math.floor(diff % 60);

      if (minutes < 10) {
        minutes = '0' + minutes;
      }

      this.data.route_time = hours + 'h ' + minutes + 'm';
    },
    togglePickers (pickerRef) {
      let refs = Object.keys(this.$refs);

      for (let i = 0; i < refs.length; i++) {
        if (refs[i].substr(0, 18) === 'date-range-picker-' && refs[i] !== pickerRef) {
          this.$refs[refs[i]].togglePicker(false, false);
        }
      }
    },
    setHandler (type, handlerID) {
      if (type === 'arrival') {
        this.setArrivalHandler(handlerID);
      } else if (type === 'departure') {
        this.setDepartureHandler(handlerID);
      }
    },
    setNotRequired (type, value) {
      Vue.set(this.data, type + '_not_required', value);
    },
    setTransport (field, value) {
      Vue.set(this.data, field, value);
    },
    setHotel (field, value) {
      Vue.set(this.data, field, value);
    },
    setArrivalHandler (handlerID) {
      Vue.set(this.data, 'arrival_handler_id', handlerID);
    },
    setDepartureHandler (handlerID) {
      Vue.set(this.data, 'departure_handler_id', handlerID);
    },
    updatePaxNumber (newValue, diff = false) {
      if (diff) {
        this.real_pax_number += newValue;
      } else {
        this.real_pax_number = newValue;
      }
    },
    updateTbcCount (newValue) {
      this.data.tbc_count = parseInt(newValue, 10);
    },
    scrollTo (sectionID) {
      document.getElementById(sectionID).scrollIntoView({ behavior: 'smooth', block: 'center' });

      setTimeout(() => {
        document.getElementById(sectionID).classList.add('is-activated');

        setTimeout(() => {
          document.getElementById(sectionID).classList.remove('is-activated');
        }, 1000);
      }, 250);
    },
    showCrewPopup () {
      this.displayCrewPopup = true;
    },
    closeCrewPopup () {
      this.displayCrewPopup = false;
    },
    showPassengerPopup () {
      this.displayPassengerPopup = true;
    },
    closePassengerPopup () {
      this.displayPassengerPopup = false;
    },
    initPickerMonths() {
      let pickersList = [
        {
          ref: 'date-range-picker-srdt',
          model: 'slot_departure_requested_date',
          fallback: 'departure_date'
        },
        {
          ref: 'date-range-picker-sgdt',
          model: 'slot_departure_granted_date',
          fallback: 'departure_date'
        },
        {
          ref: 'date-range-picker-srat',
          model: 'slot_arrival_requested_date',
          fallback: 'arrival_date'
        },
        {
          ref: 'date-range-picker-sgat',
          model: 'slot_arrival_granted_date',
          fallback: 'arrival_date'
        }
      ];

      pickersList.forEach(({ ref, model, fallback }) => {
        let picker = this.$refs[ref];
        let value = this.data[model];
        let fallbackDate = this.data[fallback];

        if (picker && (!value || !value.startDate) && fallbackDate && fallbackDate.startDate) {
          picker.monthDate = new Date(fallbackDate.startDate.getFullYear(), fallbackDate.startDate.getMonth(), 1);
        }
      });
    },
  },
  beforeDestroy () {
    this.unlockFlight();
    this.$bus.$off('flight-item-delete', this.removeFlight);
    this.$bus.$off('flight-item-save', this.validateForm);
    this.$bus.$off('flight-form-handler-update', this.setHandler);
    this.$bus.$off('flight-form-not-required-update', this.setNotRequired);
    this.$bus.$off('flight-form-transport-update', this.setTransport);
    this.$bus.$off('flight-form-hotel-update', this.setHotel);
    this.$bus.$off('flight-item-update-pax-number', this.updatePaxNumber);
    this.$bus.$off('flight-item-update-tbc-count', this.updateTbcCount);
    this.$bus.$off('add-crew-popup-show', this.showCrewPopup);
    this.$bus.$off('add-crew-popup-close', this.closeCrewPopup);
    this.$bus.$off('add-passenger-popup-show', this.showPassengerPopup);
    this.$bus.$off('add-passenger-popup-close', this.closePassengerPopup);
  }
}
</script>

<style lang="scss">
.flight-form {
  padding-top: 100px;

  .vs__dropdown-toggle {
    border: 1px solid #ddd!important;
  }

  .vue-daterange-picker .form-control {
    border: 1px solid #ddd!important;
    height: 36px;
    line-height: 24px;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .common-form {
    &.is-locked {
      opacity: .5;
      pointer-events: none;
    }
  }

  .has-inactive-date-range {
    .vue-daterange-picker {
      cursor: not-allowed;
      opacity: .75;

      .form-control {
        background: #f0f0f0;
        cursor: not-allowed;
      }
    }
  }

  .is-activated {
    & > fieldset {
      position: relative;

      &:before {
        animation: blinkedBg 1s linear;
        border-radius: 10px;
        content: "";
        height: calc(100% + 70px);
        left: -20px;
        pointer-events: none;
        position: absolute;
        top: -50px;
        width: calc(100% + 40px);
        z-index: 1;
      }
    }
  }
}

@keyframes blinkedBg {
  from {
    background: transparent;
  }

  12.5% {
    background: rgba(255, 255, 0, .125);
  }

  87.5% {
    background: rgba(255, 255, 0, .125);
  }

  to {
    background: transparent;
  }
}
</style>
